import React, { useState } from 'react'
import styled from 'styled-components'
import { useSpring, animated } from 'react-spring'
import { useDrag } from 'react-use-gesture'
import { COMMON, CommonProps, limitNumberWithinRange } from 'global'

import { IHand } from 'models'
import { AnimalPicker } from './AnimalPicker'
import { ColorPicker } from './ColorPicker'
import {
  Link,
  Text,
  Flex,
  Button,
  AnimalCard,
  GoodForOneOrgasmCard,
  Icon,
  AnimatedBox,
  IFlexProps,
} from 'components'
import { trackButtonInteraction } from 'api/tracking'

interface HandBuilderProps {
  selectedColor?: IHand['color']
  selectedAnimal?: IHand['animal']
  onComplete?: (handOpts: Pick<IHand, 'color' | 'animal'>) => void
  onCardRotate?: (rotation: number) => void
}

const BuilderWrapper = styled(Flex)<CommonProps & HandBuilderProps>`
  ${COMMON}
  .flip-card {
    position: absolute;
    cursor: grab;
    transform-style: preserve-3d;
    backface-visibility: hidden;
  }
`

const FlipButton = styled(Button)`
  span {
    transform: rotate(2deg);
    display: block;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 0.8),
      rgba(255, 255, 255, 0.4)
    );
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`

const CogFab = styled(Flex)`
  width: 42px;
  height: 42px;
  padding: 5px;
  display: flex;
  justify-content: center;
  border-radius: 50%;
  background: rgba(153, 119, 205, 0.4);
`

const CogButton = (props: IFlexProps) => (
  // @ts-ignore style-system and styled components "color" prop conflict
  <Flex alignItems='center' {...props}>
    <Link display='flex' alignItems='center'>
      <CogFab alignItems='center'>
        <Icon
          name='cog'
          size='16px'
          viewBox='16'
          fill='rgba(255,255,255,0.8)'
        />
      </CogFab>
      <Text color='rgba(255,255,255,0.8)' padding={2} selectable={false}>
        Change animal
      </Text>
    </Link>
  </Flex>
)

export const HandBuilder = ({
  selectedColor = 'orange',
  selectedAnimal = 'falcon',
  onComplete,
  onCardRotate,
}: HandBuilderProps) => {
  const [color, setColor] = useState(selectedColor)
  const [animal, setAnimal] = useState(selectedAnimal)
  const [editing, setEditing] = useState(false)
  const [rotation, setRotation] = useState(0)
  const [completed, setCompleted] = useState(false)
  const [scaleVal, setScaleVal] = useState('0.9')

  const { transform } = useSpring({
    transform: `perspective(1000px) rotateY(${rotation}deg) scale(${scaleVal})`,
    onRest: () => {
      if (onComplete && !completed && Math.abs(rotation) === 180) {
        onComplete({ color, animal })
        // Prevent multiple calls, Ive tried memoizing callbacks with no success.
        setCompleted(true)
      }
    },
  })

  const { opacity } = useSpring({
    opacity: Math.abs(rotation) === 180 ? 0 : 1,
  })

  const handleFlip = (r: number) => {
    setScaleVal('1.0')
    handleRotation(r > 0 ? 180 : -180)
  }

  const handleRotation = (r: number) => {
    setRotation(r)
    if (onCardRotate) {
      onCardRotate(r)
    }
  }

  const bindDrag = useDrag(({ down, movement: [mx] }) => {
    if (editing || (down && mx === 0)) {
      return
    }
    if (!down) {
      return handleFlip(mx)
    }
    handleRotation(limitNumberWithinRange(mx, -180, 180))
  })

  return (
    <Flex
      width='100%'
      height={['100%', '100%', '100%', '50%']}
      maxHeight={['600px']}
      flexDirection={['column', 'column', 'column', 'row']}
      alignItems={['unset', 'unset', 'unset', 'center']}
    >
      <BuilderWrapper
        flexDirection='column'
        justifyContent='space-between'
        alignItems='center'
        width={['100%', '100%', '100%', '50%']}
        height={['400px']}
        {...bindDrag()}
      >
        <animated.div
          className='flip-card front'
          style={{
            transform,
          }}
        >
          <AnimalCard flipIcon mb={4} handColor={color} animal={animal} />
        </animated.div>
        <animated.div
          className='flip-card back'
          style={{
            transform: transform.interpolate((t) => `${t} rotateY(180deg)`),
          }}
        >
          <GoodForOneOrgasmCard handColor={color} animal={animal} />
        </animated.div>
        {!editing && (
          <AnimatedBox
            position='absolute'
            bottom={['-40px', '-50px']}
            mb={3}
            style={{ opacity }}
          >
            <FlipButton
              variant='link'
              onClick={() => handleFlip(rotation)}
              icon={
                <Icon
                  name='flipIcon'
                  viewBox='32'
                  size='30px'
                  fill='rgba(255,255,255,0.8)'
                  transform='translate(-122.000000, -662.000000)'
                />
              }
            >
              Flip over!
            </FlipButton>
          </AnimatedBox>
        )}
      </BuilderWrapper>
      {!editing && (
        <animated.div style={{ opacity }}>
          <Flex
            mt={['20px', 5, null, 0]}
            height={[null, null, null, '100%']}
            alignItems={[null, null, null, 'baseline']}
          >
            <CogButton
              onClick={() => {
                setEditing(true)
                trackButtonInteraction('Change Animal Start', 'Click')
              }}
            />
          </Flex>
        </animated.div>
      )}
      {editing && (
        <Flex flexDirection='column' width={['100%', '100%', '100%', '50%']}>
          <AnimalPicker
            mb={3}
            selectedAnimal={selectedAnimal}
            onAnimalSelect={(a: IHand['animal']) => {
              setAnimal(a)
              trackButtonInteraction(`Change Animal to ${a}`, 'Click')
            }}
          />
          <ColorPicker
            mb={1}
            selectedColor={selectedColor}
            onColorSelect={(c: IHand['color']) => setColor(c)}
          />
          <Flex justifyContent='center'>
            <Button
              full
              maxWidth='290px'
              backgroundColor='transparent'
              color='white'
              uiColorTheme='dark'
              variant='link'
              onClick={() => {
                setEditing(false)
                trackButtonInteraction('Change Animal Finish', 'Click')
              }}
            >
              Done
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  )
}
